import { format, isAfter, isSameDay, startOfWeek } from 'date-fns'
import { ko } from 'date-fns/locale'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Icon } from '~/@common/ui/Icon/Icon'

import type { Week } from './getMonthWeeks'
import { getMonthWeeks } from './getMonthWeeks'
import { S } from './WeeklyCalendar.style'

type Props = {
  selectedWeekStart: Date | null
  onWeekSelect: (startDate: Date, endDate: Date) => void
  maxDate?: Date
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1)

function formatWeekLabel(week: Week): string {
  const startStr = format(week.startDate, 'M/d')
  const startDay = format(week.startDate, 'EEEE', { locale: ko })
  const endStr = format(week.endDate, 'M/d')
  const endDay = format(week.endDate, 'EEEE', { locale: ko })
  return `${week.weekNumber}주차(${startStr} ${startDay} ~ ${endStr} ${endDay})`
}

export default function WeeklyCalendar({ selectedWeekStart, onWeekSelect, maxDate }: Props) {
  const base = selectedWeekStart ?? new Date()
  const [viewingYear, setViewingYear] = useState(base.getFullYear())
  const [viewingMonth, setViewingMonth] = useState(base.getMonth() + 1)
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const currentWeekMonday = startOfWeek(maxDate ?? new Date(), { weekStartsOn: 1 })
  const weeks = getMonthWeeks(viewingYear, viewingMonth)
  const YEARS = useMemo(() => {
    const currentYear = (maxDate ?? new Date()).getFullYear()
    return Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i)
  }, [maxDate])

  const isDisabled = (weekStart: Date) => isAfter(weekStart, currentWeekMonday)
  const isSelected = (weekStart: Date) =>
    selectedWeekStart !== null && isSameDay(weekStart, selectedWeekStart)

  const prevMonth =
    viewingMonth === 1
      ? { year: viewingYear - 1, month: 12 }
      : { year: viewingYear, month: viewingMonth - 1 }
  const nextMonth =
    viewingMonth === 12
      ? { year: viewingYear + 1, month: 1 }
      : { year: viewingYear, month: viewingMonth + 1 }

  const selectedYearRef = useRef<HTMLButtonElement>(null)
  const selectedMonthRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isPickerOpen) {
      selectedYearRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' })
      selectedMonthRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' })
    }
  }, [isPickerOpen])

  return (
    <S.Root>
      <div className="header">
        <button
          type="button"
          className="nav-btn"
          onClick={() => {
            setViewingYear(prevMonth.year)
            setViewingMonth(prevMonth.month)
          }}
        >
          <Icon name="icon_chevron_left" size={14} color="var(--White)" />
          {prevMonth.month}월
        </button>
        <button
          type="button"
          className="month-btn"
          onClick={() => setIsPickerOpen((prev) => !prev)}
        >
          {viewingYear}년 {viewingMonth}월
          <span className={`chevron-icon${isPickerOpen ? ' open' : ''}`}>
            <Icon name="icon_chevron_down" size={16} color="var(--White)" />
          </span>
        </button>
        <button
          type="button"
          className="nav-btn"
          onClick={() => {
            setViewingYear(nextMonth.year)
            setViewingMonth(nextMonth.month)
          }}
        >
          {nextMonth.month}월
          <Icon name="icon_chevron_right" size={14} color="var(--White)" />
        </button>
      </div>

      <div className="body">
        {isPickerOpen ? (
          <div className="picker">
            <div className="picker-column">
              {YEARS.map((year) => (
                <button
                  key={year}
                  ref={year === viewingYear ? selectedYearRef : undefined}
                  type="button"
                  className={`picker-item${year === viewingYear ? ' selected' : ''}`}
                  onClick={() => setViewingYear(year)}
                >
                  {year}년
                </button>
              ))}
            </div>
            <div className="picker-column">
              {MONTHS.map((month) => (
                <button
                  key={month}
                  ref={month === viewingMonth ? selectedMonthRef : undefined}
                  type="button"
                  className={`picker-item${month === viewingMonth ? ' selected' : ''}`}
                  onClick={() => {
                    setViewingMonth(month)
                    setIsPickerOpen(false)
                  }}
                >
                  {month}월
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="week-list">
              {weeks.map((week) => {
                const disabled = isDisabled(week.startDate)
                const selected = isSelected(week.startDate)
                return (
                  <button
                    key={week.weekNumber}
                    type="button"
                    className={`week-item${selected ? ' selected' : ''}`}
                    disabled={disabled}
                    onClick={() => onWeekSelect(week.startDate, week.endDate)}
                  >
                    {formatWeekLabel(week)}
                  </button>
                )
              })}
            </div>
            <p className="note">
              매월 첫 목요일이 포함된 주가 해당 월의 1주차입니다.
              <span className="note-standard"> (국제 표준)</span>
            </p>
          </>
        )}
      </div>
    </S.Root>
  )
}
